import { addDays, format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import { SessionStatus } from '@/types';
import { Alert, CircularProgress, Typography } from '@/ui';

import SessionInfoPopover from '../SessionInfoPopover';
import {
  bucketByDay,
  bucketEventsByDay,
  dayKeyFor,
  dayKeyFromIso,
  formatIsoTime,
  getMonthMatrix,
  getMonthRange,
} from '../calendarGrid';
import {
  MONTH_DAY_CELL_CLASS,
  MONTH_DAY_CELL_TODAY_CLASS,
  MONTH_HEADER_ROW_CLASS,
  MONTH_WEEKDAY_HEADING_CLASS,
} from '../calendarStyles';
import {
  ADMIN_NATIVE_LOAD_ERROR_MESSAGE,
  PARTICIPANT_NATIVE_LOAD_ERROR_MESSAGE,
  useCalendarWindow,
} from '../useCalendarWindow';
import { useSessionActions } from '../useSessionActions';

import type { ScheduleViewProps } from '../types';
import type { AppointmentActionRecord } from '@/features/appointments/appointmentActionRecord';
import type { FC, MouseEvent } from 'react';

import {
  actionRecordId,
  actionRecordIsConfirmed,
  actionRecordLifecycle,
  actionRecordMember,
  actionRecordSessionSubject,
  actionRecordStartAt,
  actionRecordSubject,
  isNativeAppointmentActionRecord,
} from '@/features/appointments/appointmentActionRecord';

const WEEKDAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const MonthView: FC<ScheduleViewProps> = ({
  adminCoachId,
  coachId,
  onTimeZoneChange,
  selectedMemberId,
  timeZone: fallbackTimeZone,
  viewDate,
}) => {
  const weeks = getMonthMatrix(viewDate, fallbackTimeZone);
  const [gridStart, gridEnd] = getMonthRange(viewDate, fallbackTimeZone);
  const {
    actionRecords,
    adminNativeLoadError,
    events,
    isLoading,
    participantNativeLoadError,
    timeZone: calendarTimeZone,
  } = useCalendarWindow(
    coachId,
    gridStart,
    gridEnd,
    selectedMemberId,
    fallbackTimeZone,
    adminCoachId,
  );
  const timeZone = adminCoachId
    ? fallbackTimeZone
    : (calendarTimeZone ?? fallbackTimeZone);
  const todayKey = dayKeyFromIso(new Date().toISOString(), timeZone);
  const actions = useSessionActions(timeZone, {
    adminCoachId,
    disabled: adminNativeLoadError || participantNativeLoadError,
  });

  useEffect(() => {
    if (adminCoachId) return;
    onTimeZoneChange?.(calendarTimeZone);
  }, [adminCoachId, calendarTimeZone, onTimeZoneChange]);

  // MEN-4695 slice 5: match WeekView slice-4 semantics — iterate
  // AppointmentActionRecords and exclude records that shouldn't render
  // as clickable session dots. List view continues to show cancelled
  // records with an explicit "Cancelled by ..." status pill.
  //
  // MEN-4695 (review finding, MonthView.tsx:74): natives must gate on
  // BOTH `bookingStatus === 'confirmed'` AND
  // `lifecycleStatus !== 'cancelled'` — a `pending` or `rejected`
  // Appointment is not a real bookable session.
  const gridRecords = useMemo(
    () =>
      actionRecords.filter((record) => {
        if (isNativeAppointmentActionRecord(record)) {
          return (
            actionRecordIsConfirmed(record) &&
            actionRecordLifecycle(record) !== 'cancelled'
          );
        }
        return record.status !== SessionStatus.CANCELLED;
      }),
    [actionRecords],
  );
  const recordsByDay = useMemo(
    () =>
      bucketByDay(
        gridRecords,
        (record) => actionRecordStartAt(record),
        timeZone,
      ),
    [gridRecords, timeZone],
  );
  const eventsByDay = useMemo(
    () => bucketEventsByDay(events, timeZone),
    [events, timeZone],
  );

  const [popoverRecord, setPopoverRecord] =
    useState<AppointmentActionRecord | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!adminNativeLoadError) return;
    setPopoverRecord(null);
    setAnchorEl(null);
  }, [adminNativeLoadError]);

  const openPopover = (
    record: AppointmentActionRecord,
    event: MouseEvent<HTMLElement>,
  ) => {
    setPopoverRecord(record);
    setAnchorEl(event.currentTarget);
  };
  const closePopover = () => {
    setPopoverRecord(null);
    setAnchorEl(null);
  };

  if (isLoading)
    return <CircularProgress data-testid="schedule-month-loading" />;
  if (adminNativeLoadError) {
    return <Alert severity="error">{ADMIN_NATIVE_LOAD_ERROR_MESSAGE}</Alert>;
  }
  if (participantNativeLoadError) {
    return (
      <Alert severity="error">{PARTICIPANT_NATIVE_LOAD_ERROR_MESSAGE}</Alert>
    );
  }

  return (
    <>
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- Keyboard users need to focus and scroll the constrained month grid. */}
      <div
        className="h-full overflow-auto bg-white"
        data-testid="schedule-month-view"
        tabIndex={0}
      >
        <div className="min-w-[48rem]">
          <div
            className={MONTH_HEADER_ROW_CLASS}
            data-testid="schedule-month-header"
          >
            {WEEKDAY_LABELS.map((label) => (
              <div
                key={label}
                className={MONTH_WEEKDAY_HEADING_CLASS}
                data-testid="schedule-month-weekday-heading"
              >
                <Typography variant="caption">{label}</Typography>
              </div>
            ))}
          </div>

          {weeks.map((week, weekIndex) => (
            <div
              key={addDays(gridStart, weekIndex * 7).toISOString()}
              className="grid grid-cols-7"
            >
              {week.map((day) => {
                const inMonth = day.getMonth() === viewDate.getMonth();
                const isToday = dayKeyFor(day) === todayKey;
                const dayRecords = recordsByDay.get(dayKeyFor(day)) ?? [];
                const dayEvents = eventsByDay.get(dayKeyFor(day)) ?? [];
                return (
                  <div
                    key={day.toISOString()}
                    className={`min-h-24 p-1 ${isToday ? MONTH_DAY_CELL_TODAY_CLASS : MONTH_DAY_CELL_CLASS} ${inMonth ? '' : 'bg-gray-50 text-gray-400'}`}
                    data-testid="schedule-month-day-cell"
                    data-today={isToday ? 'true' : undefined}
                  >
                    <Typography variant="caption">
                      {format(day, 'd')}
                    </Typography>

                    {dayEvents
                      .filter((event) => event.allDay)
                      .map((event) => (
                        <div
                          key={event.id}
                          className="mt-1 truncate rounded bg-yellow-100 px-1 text-xs"
                          data-testid="schedule-month-event"
                        >
                          Busy
                        </div>
                      ))}

                    {dayRecords.map((record) => (
                      <button
                        key={actionRecordId(record)}
                        className="mt-1 flex min-h-6 w-full appearance-none items-center gap-1 overflow-hidden border-0 bg-transparent p-0 text-left text-xs leading-none"
                        data-testid="schedule-month-session"
                        type="button"
                        onClick={(event) => openPopover(record, event)}
                      >
                        <span
                          aria-hidden="true"
                          className="size-2 shrink-0 rounded-full bg-sage-300"
                          data-testid="schedule-month-session-dot"
                        />
                        <span className="shrink-0">
                          {formatIsoTime(actionRecordStartAt(record), timeZone)}
                        </span>
                        <span className="truncate font-medium">
                          {actionRecordMember(record).displayName}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <SessionInfoPopover
          adminMode={Boolean(adminCoachId)}
          anchorEl={anchorEl}
          session={
            popoverRecord ? actionRecordSessionSubject(popoverRecord) : null
          }
          subject={
            popoverRecord ? actionRecordSubject(popoverRecord) : undefined
          }
          timeZone={timeZone}
          onCancel={(subject) => {
            const focusTarget = anchorEl;
            closePopover();
            actions
              .openCancel(subject, () => focusTarget?.focus())
              .catch(() => undefined);
          }}
          onClose={closePopover}
          onReschedule={(subject, returnFocus) => {
            closePopover();
            actions.openReschedule(subject, returnFocus);
          }}
        />
        {actions.modals}
      </div>
      {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
    </>
  );
};

export default MonthView;
